import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { classNames } from "../../helpers/classNames";
import Logo from "../../assets/images/logo-memory-doodle.svg";
import SavePill from "./SavePill";

/**
 * The customer header (docs/design/HANDOFF.md, "Header").
 *
 * Mobile and tablet: 56px, logo centred between two 88px slots so it never
 * shifts when Back appears. Mobile shows a chevron with the word "Back";
 * tablet shows the chevron only (its bottom bar has a Back button). Desktop
 * (lg): one row, logo left, progress in the middle, menu on the right. Between
 * lg and xl the logo, gaps and stepper are a size smaller so the five steps
 * fit on that one row at 1024px.
 *
 * `back`     {to} or {onClick}, plus an optional label and `disabled`; null for no Back.
 * `menu`     the MenuButton (or anything) for the right slot; null to leave it empty.
 * `progress` the Progress component: a row under the header on mobile and
 *            tablet, inline on desktop.
 * `logoTo` / `logoHref` make the logo a link; neither leaves it plain.
 */
const Header = ({ back = null, menu = null, progress = null, logoTo, logoHref, sticky = true, className }) => {
  const navigate = useNavigate();

  const logo = <img src={Logo} alt="Memory Doodle" className="block h-[34px] w-auto lg:h-9 xl:h-11" />;
  const logoNode = logoTo ? (
    <Link to={logoTo} className="inline-flex" aria-label="Memory Doodle home">
      {logo}
    </Link>
  ) : logoHref ? (
    <a href={logoHref} className="inline-flex" aria-label="Memory Doodle home">
      {logo}
    </a>
  ) : (
    logo
  );

  const backNode = back ? (
    <button
      type="button"
      onClick={() => (back.onClick ? back.onClick() : back.to ? navigate(back.to) : navigate(-1))}
      disabled={!!back.disabled}
      className="inline-flex h-11 disabled:opacity-40 min-w-[44px] items-center gap-0.5 pl-0.5 pr-2 text-base font-semibold text-md-dark rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink"
      aria-label={back.label || "Back"}
    >
      <ChevronLeft className="h-[22px] w-[22px]" aria-hidden="true" />
      <span className="md:sr-only">{back.label || "Back"}</span>
    </button>
  ) : null;

  return (
    <header
      className={classNames(
        "z-50 w-full bg-white border-b border-md-pink-light-1",
        sticky ? "sticky top-0" : "relative",
        className
      )}
    >
      {/* Mobile and tablet: three slots, then the progress row. */}
      <div className="lg:hidden">
        <div className="grid h-14 grid-cols-[88px_1fr_88px] items-center px-1.5">
          <div className="flex items-center justify-start">{backNode}</div>
          <div className="flex items-center justify-center">{logoNode}</div>
          <div className="flex items-center justify-end">{menu}</div>
        </div>
        {progress ? <div className="px-4 pb-3 md:px-6 md:pb-4">{progress}</div> : null}
      </div>

      {/* Desktop: one row. */}
      <div className="hidden lg:flex mx-auto min-h-[72px] max-w-[1200px] items-center gap-4 px-8 py-3.5 xl:gap-6">
        <div className="flex flex-shrink-0 items-center">{logoNode}</div>
        <div className="flex min-w-0 flex-1 justify-center">{progress}</div>
        <div className="flex flex-shrink-0 items-center justify-end">{menu}</div>
      </div>

      {/* The "Saved" pill floats just below the header on every customer screen. */}
      <SavePill />
    </header>
  );
};

export default Header;
